import { Loader2 } from 'lucide-react'
import { useTranslation } from '@/hooks/use-translation'
import type { ChatMessage, ToolCallResult } from '../types/agent.types'
import { BrandVoicesCard } from './tool-result-cards/brand-voices-card'
import { ContentStatsCard } from './tool-result-cards/content-stats-card'
import { FramerAddItemCard } from './tool-result-cards/framer-add-item-card'
import { FramerPublishCard } from './tool-result-cards/framer-publish-card'
import { GscPageSearchPerformanceCard } from './tool-result-cards/gsc-page-search-performance-card'
import { LanguageUpdatedCard } from './tool-result-cards/language-updated-card'

type Props = {
  message: ChatMessage
}

function renderToolCard(toolCall: ToolCallResult, t: ReturnType<typeof useTranslation>) {
  if (toolCall.status === 'running') {
    return (
      <div
        key={toolCall.toolUseId}
        className="flex items-center gap-2 text-sm text-muted-foreground my-1.5"
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        <span>{t('agent.tools.running', { tool: toolCall.tool.replace(/_/g, ' ') })}</span>
      </div>
    )
  }

  const result = toolCall.result as Record<string, unknown> | null | undefined

  switch (toolCall.tool) {
    case 'list_brand_voices':
      return <BrandVoicesCard key={toolCall.toolUseId} result={result as never} />
    case 'get_content_stats':
      return <ContentStatsCard key={toolCall.toolUseId} result={result as never} />
    case 'gsc_get_page_search_performance':
      return <GscPageSearchPerformanceCard key={toolCall.toolUseId} result={result as never} />
    case 'update_user_language':
      return <LanguageUpdatedCard key={toolCall.toolUseId} result={result as never} />
    case 'framer_add_item':
      return <FramerAddItemCard key={toolCall.toolUseId} result={result as never} />
    case 'framer_publish':
      return <FramerPublishCard key={toolCall.toolUseId} result={result as never} />
    default:
      return null
  }
}

function InlineMarkdown({ text }: { text: string }) {
  const parts: React.ReactNode[] = []
  // Match bold, italic, inline code
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/g
  let lastIndex = 0

  let match = regex.exec(text)
  while (match !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }
    if (match[2]) {
      parts.push(
        <strong key={match.index} className="font-semibold">
          {match[2]}
        </strong>,
      )
    } else if (match[3]) {
      parts.push(
        <em key={match.index} className="italic">
          {match[3]}
        </em>,
      )
    } else if (match[4]) {
      parts.push(
        <code key={match.index} className="bg-muted px-1 py-0.5 rounded text-[0.85em] font-mono">
          {match[4]}
        </code>,
      )
    }
    lastIndex = match.index + match[0].length
    match = regex.exec(text)
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return <>{parts}</>
}

function renderMarkdown(text: string) {
  if (!text) return null

  const lines = text.split('\n')
  const elements: React.ReactNode[] = []
  let i = 0
  let blockIndex = 0

  while (i < lines.length) {
    const line = lines[i]
    if (line === undefined) break

    if (/^[-*]\s+/.test(line)) {
      const items: string[] = []
      while (i < lines.length) {
        const currentLine = lines[i]
        if (currentLine === undefined || !/^[-*]\s+/.test(currentLine)) break
        items.push(currentLine.replace(/^[-*]\s+/, ''))
        i++
      }
      elements.push(
        <ul key={`ul-${blockIndex}`} className="list-disc list-inside my-1 space-y-0.5">
          {items.map((item) => (
            <li key={item}>
              <InlineMarkdown text={item} />
            </li>
          ))}
        </ul>,
      )
      blockIndex++
      continue
    }

    if (/^\d+\.\s+/.test(line)) {
      const items: string[] = []
      while (i < lines.length) {
        const currentLine = lines[i]
        if (currentLine === undefined || !/^\d+\.\s+/.test(currentLine)) break
        items.push(currentLine.replace(/^\d+\.\s+/, ''))
        i++
      }
      elements.push(
        <ol key={`ol-${blockIndex}`} className="list-decimal list-inside my-1 space-y-0.5">
          {items.map((item) => (
            <li key={item}>
              <InlineMarkdown text={item} />
            </li>
          ))}
        </ol>,
      )
      blockIndex++
      continue
    }

    // Empty line = spacing
    if (line.trim() === '') {
      elements.push(<br key={`br-${blockIndex}`} />)
      blockIndex++
      i++
      continue
    }

    // Regular paragraph
    elements.push(
      <p key={`p-${blockIndex}`} className="my-0.5">
        <InlineMarkdown text={line} />
      </p>,
    )
    blockIndex++
    i++
  }

  return <>{elements}</>
}

function shouldRenderAssistantText(message: ChatMessage) {
  if (!message.text) return false

  const hasGscCard = message.toolCalls.some(
    (toolCall) => toolCall.tool === 'gsc_get_page_search_performance',
  )

  if (!hasGscCard) return true

  const normalizedText = message.text.trim()
  const looksLikeStructuredDump =
    normalizedText.includes('|') ||
    normalizedText.includes('Top keywords') ||
    normalizedText.includes('Top queries') ||
    normalizedText.includes('Métricas de GSC') ||
    normalizedText.includes('Google Search Console')

  if (looksLikeStructuredDump) {
    return normalizedText.length <= 220
  }

  return normalizedText.length <= 220
}

export function AgentMessage({ message }: Props) {
  const t = useTranslation()
  const isUser = message.role === 'user'

  // Streaming with no text: bouncing dots
  if (message.isStreaming && !message.text && message.toolCalls.length === 0) {
    return (
      <div className="flex justify-start">
        <div className="bg-muted/50 border border-border text-card-foreground rounded-2xl rounded-tl-md px-4 py-3 max-w-[85%]">
          <div className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce [animation-delay:0ms]" />
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce [animation-delay:150ms]" />
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce [animation-delay:300ms]" />
          </div>
        </div>
      </div>
    )
  }

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="bg-primary text-primary-foreground rounded-2xl rounded-tr-md px-4 py-2.5 max-w-[85%] text-sm">
          {message.text}
        </div>
      </div>
    )
  }

  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] space-y-1">
        {message.toolCalls.map((tc) => renderToolCard(tc, t))}
        {shouldRenderAssistantText(message) && (
          <div className="bg-muted/50 border border-border text-card-foreground rounded-2xl rounded-tl-md px-4 py-2.5 text-sm leading-relaxed">
            {renderMarkdown(message.text)}
          </div>
        )}
      </div>
    </div>
  )
}
